import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import { Visibility, VisibilityOff, Phone, Lock, Verified, Gavel, ExpandMore, Translate } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SignaturePad from '../../components/common/SignaturePad';

const steps = ['Create Account', 'Verify OTP', 'Signature & Agreement'];

// Area Manager Rules in Hindi and English (8 rules)
const areaManagerRules = {
  hindi: [
    {
      title: 'नियम 1: रजिस्ट्रेशन फीस अनिवार्य',
      content: 'एरिया मैनेजर के लिए रजिस्ट्रेशन फीस पे करनी अनिवार्य है तभी आपको मैनेजर डैशबोर्ड दिया जाएगा। यह फीस नॉन रिफंडेबल रहेगी।',
    },
    {
      title: 'नियम 2: पार्टनर ऐड करने की समय सीमा',
      content: 'अगर आप स्टार्टिंग के 2 महीने में 300 न्यू पार्टनर और 1 साल में 1500 पार्टनर को ऐड नहीं कर पाए तो कंपनी कभी भी आपको एरिया मैनेजर से निलंबित कर सकती है।',
    },
    {
      title: 'नियम 3: विज्ञापन की शर्त',
      content: 'जब आप अपने एरिया में 300 न्यू पार्टनर को ऐड कर लेंगे तभी कंपनी आपके लिए एडवर्टाइजमेंट करेगी।',
    },
    {
      title: 'नियम 4: पार्टनर की देखरेख और कमीशन',
      content: 'आपके एरिया में जो भी पार्टनर काम कर रहे हैं उन सभी की देखरेख और कंपनी की कमीशन पे करवाने का जिम्मेवारी आपकी होगी।',
    },
    {
      title: 'नियम 5: कमीशन प्राप्ति की शर्त',
      content: 'जो पार्टनर कंपनी की कमीशन को पे कर देगा तभी आपको कमीशन मिलेगी। अगर पार्टनर किसी कारणवश कंपनी की कमीशन पे नहीं करता तो आपको उस काम की कोई भी कमीशन नहीं मिलेगी।',
    },
    {
      title: 'नियम 6: साइट विज़िट अनिवार्य',
      content: 'आपके एरिया में चल रहे काम पर जब भी कंपनी आपको निर्देश देगी आपको जाना होगा।',
    },
    {
      title: 'नियम 7: नियमों का पालन न करने पर कार्रवाई',
      content: 'अगर आप इस प्रकार की कोई भी रूल्स को फॉलो नहीं करते तो कंपनी आपकी प्रोफाइल को कभी भी बंद कर सकती है।',
    },
    {
      title: 'नियम 8: आय प्रदर्शन पर निर्भर',
      content: 'आपकी कमाई Performance पर निर्भर करेगी, कोई गारंटीड आय नहीं है।',
    },
  ],
  english: [
    {
      title: 'Rule 1: Registration Fee Mandatory',
      content: 'Payment of registration fee is mandatory for Area Manager. Only then you will be given a manager dashboard. This fee is non-refundable.',
    },
    {
      title: 'Rule 2: Partner Addition Timeline',
      content: 'If you fail to add 300 new partners within the first 2 months and 1500 partners within 1 year, the company may suspend you from the position of Area Manager at any time.',
    },
    {
      title: 'Rule 3: Advertisement Condition',
      content: 'Only after you add 300 new partners in your area will the company run advertisements for you.',
    },
    {
      title: 'Rule 4: Partner Supervision & Commission Collection',
      content: 'You will be responsible for overseeing all partners working in your area and ensuring they pay the company commission.',
    },
    {
      title: 'Rule 5: Commission Eligibility',
      content: 'You will receive commission only when the partner pays the company commission. If a partner does not pay the company commission for any reason, you will not receive any commission for that work.',
    },
    {
      title: 'Rule 6: Site Visit Mandatory',
      content: 'Whenever the company instructs you regarding work in your area, you must visit the site.',
    },
    {
      title: 'Rule 7: Non-Compliance Consequences',
      content: 'If you fail to follow any of these rules, the company may close your profile at any time.',
    },
    {
      title: 'Rule 8: Performance-Based Earnings',
      content: 'Your earnings will depend on performance. There is no guaranteed income.',
    },
  ],
};

const Register = () => {
  const navigate = useNavigate();
  const { signup, verifyOtp, resendOtp, submitAgreement } = useAuth();

  // Step control
  const [activeStep, setActiveStep] = useState(0);

  // Language toggle
  const [language, setLanguage] = useState('hindi'); // 'hindi' or 'english'

  // Form data
  const [formData, setFormData] = useState({
    mobileNumber: '',
    password: '',
    confirmPassword: '',
  });

  // OTP
  const [otp, setOtp] = useState(['', '', '', '']);

  // Signature
  const [signature, setSignature] = useState(null);
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);

  // Agreement
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);

  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  // OTP countdown timer
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only last character
    setOtp(newOtp);

    // Auto-advance to next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Move back on backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const validateForm = () => {
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  // Step 1: Submit registration (mobile + password)
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await signup(formData.mobileNumber, formData.password);
      console.log('Signup response:', response);

      // Show success and move to OTP step
      setSuccess('OTP has been sent to your mobile number!');
      setOtpTimer(120); // 2 minute timer for resend

      // Move to OTP step after short delay
      setTimeout(() => {
        setActiveStep(1);
        setSuccess('');
      }, 1000);
    } catch (error) {
      console.error('Registration error:', error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          'Registration failed. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const otpCode = otp.join('');
    if (otpCode.length !== 4) {
      setError('Please enter the complete 4-digit OTP');
      return;
    }

    setLoading(true);

    try {
      const response = await verifyOtp(formData.mobileNumber, otpCode);
      console.log('OTP verification response:', response);

      setSuccess('OTP verified successfully!');

      // Move to signature & agreement step
      setTimeout(() => {
        setActiveStep(2);
        setSuccess('');
      }, 1000);
    } catch (error) {
      console.error('OTP verification error:', error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.description ||
          'Invalid OTP. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setError('');
    setLoading(true);

    try {
      await resendOtp(formData.mobileNumber);
      setSuccess('OTP resent successfully!');
      setOtpTimer(120); // Reset timer
      setOtp(['', '', '', '']);
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError(
        error.response?.data?.message || 'Failed to resend OTP. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Submit signature and agreement
  const handleSubmitAgreement = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!signature) {
      setError(language === 'hindi' ? 'कृपया अपना हस्ताक्षर प्रदान करें' : 'Please provide your signature');
      return;
    }
    if (!signatureConfirmed) {
      setError(language === 'hindi' ? 'कृपया "Confirm Signature" पर क्लिक करके अपने हस्ताक्षर की पुष्टि करें' : 'Please confirm your signature by clicking "Confirm Signature"');
      return;
    }
    if (!agreedToTerms) {
      setError(language === 'hindi' ? 'कृपया एरिया मैनेजर नियमों और शर्तों को स्वीकार करें' : 'Please accept the Area Manager rules and conditions');
      return;
    }
    if (!agreedToPrivacy) {
      setError(language === 'hindi' ? 'कृपया गोपनीयता नीति स्वीकार करें' : 'Please accept the privacy policy');
      return;
    }

    setLoading(true);

    try {
      // Store the language preference as the agreed version
      const agreedVersion = `1.0_${language}`;
      const response = await submitAgreement(signature, true, agreedVersion);
      console.log('Agreement submission response:', response);

      setSuccess(language === 'hindi' ? 'समझौता सफलतापूर्वक सबमिट हुआ!' : 'Agreement submitted successfully!');

      // Navigate to document upload page
      setTimeout(() => {
        navigate('/document-upload');
      }, 1500);
    } catch (error) {
      console.error('Agreement submission error:', error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          (language === 'hindi' ? 'समझौता सबमिट करने में विफल। कृपया पुनः प्रयास करें।' : 'Failed to submit agreement. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureSave = (signatureDataUrl) => {
    setSignature(signatureDataUrl);
    setSignatureConfirmed(!!signatureDataUrl);
  };

  const renderStep1 = () => (
    <form onSubmit={handleRegister}>
      <TextField
        fullWidth
        label="Mobile Number"
        name="mobileNumber"
        value={formData.mobileNumber}
        onChange={handleChange}
        margin="normal"
        placeholder="Enter 10-digit mobile number"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Phone color="action" />
            </InputAdornment>
          ),
        }}
        disabled={loading}
        helperText="Enter mobile number without +91 prefix"
        inputProps={{ maxLength: 10 }}
      />

      <TextField
        fullWidth
        label="Password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={handleChange}
        margin="normal"
        placeholder="Create a strong password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        disabled={loading}
        helperText="Minimum 6 characters"
      />

      <TextField
        fullWidth
        label="Confirm Password"
        name="confirmPassword"
        type={showConfirmPassword ? 'text' : 'password'}
        value={formData.confirmPassword}
        onChange={handleChange}
        margin="normal"
        placeholder="Re-enter your password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        disabled={loading}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 3, mb: 2, py: 1.5 }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
      </Button>
    </form>
  );

  const renderStep2 = () => (
    <form onSubmit={handleVerifyOtp}>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Enter the 4-digit OTP sent to{' '}
        <strong>{formData.mobileNumber}</strong>
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 3 }}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            id={`otp-input-${index}`}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '1.5rem',
                width: '3rem',
                height: '3rem',
                padding: '0.5rem',
              },
            }}
            variant="outlined"
            disabled={loading}
            autoFocus={index === 0}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        ))}
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 1, mb: 2, py: 1.5 }}
        disabled={loading || otp.join('').length !== 4}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        {otpTimer > 0 ? (
          <Typography variant="body2" color="text.secondary">
            Resend OTP in <strong>{otpTimer}</strong> seconds
          </Typography>
        ) : (
          <Link
            component="button"
            variant="body2"
            onClick={handleResendOtp}
            sx={{ cursor: 'pointer' }}
            disabled={loading}
          >
            Resend OTP
          </Link>
        )}
      </Box>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            setActiveStep(0);
            setError('');
            setSuccess('');
            setOtp(['', '', '', '']);
          }}
          sx={{ cursor: 'pointer' }}
        >
          Change mobile number
        </Link>
      </Box>
    </form>
  );

  const renderStep3 = () => {
    const rules = language === 'hindi' ? areaManagerRules.hindi : areaManagerRules.english;
    const langToggleLabel = language === 'hindi' ? 'English में देखें' : 'हिंदी में देखें';

    return (
      <form onSubmit={handleSubmitAgreement}>
        {/* Policy Information Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Gavel sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              {language === 'hindi' ? 'समझौता एवं हस्ताक्षर' : 'Agreement & Signature'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {language === 'hindi'
              ? 'कृपया एरिया मैनेजर के सभी 8 नियमों की समीक्षा करें, अपना डिजिटल हस्ताक्षर प्रदान करें और आगे बढ़ने के लिए नियमों को स्वीकार करें।'
              : 'Please review all 8 Area Manager rules, provide your digital signature, and accept the rules to continue.'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Language Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Chip
            icon={<Translate />}
            label={langToggleLabel}
            variant="outlined"
            color="primary"
            onClick={() => setLanguage(language === 'hindi' ? 'english' : 'hindi')}
            clickable
            sx={{ fontWeight: 500 }}
          />
        </Box>

        {/* Area Manager Rules Accordion */}
        <Accordion
          expanded={termsExpanded}
          onChange={() => setTermsExpanded(!termsExpanded)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2" fontWeight="bold">
              {language === 'hindi'
                ? `एरिया मैनेजर नियम व शर्तें (कुल ${rules.length} नियम)`
                : `Area Manager Rules & Conditions (Total ${rules.length} rules)`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {rules.map((rule, index) => (
              <Box key={index} sx={{ mb: index < rules.length - 1 ? 2.5 : 1 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="error.main"
                  gutterBottom
                >
                  {rule.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {rule.content}
                </Typography>
                {index < rules.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>

        {/* Signature Pad */}
        <Typography variant="subtitle2" gutterBottom>
          {language === 'hindi' ? 'डिजिटल हस्ताक्षर' : 'Digital Signature'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {language === 'hindi'
            ? 'कृपया नीचे दिए गए बॉक्स में माउस या फिंगर (टच डिवाइस पर) से हस्ताक्षर करें'
            : 'Please sign in the box below using your mouse or finger (on touch devices)'}
        </Typography>

        <SignaturePad onSave={handleSignatureSave} disabled={loading} />

        {signature && signatureConfirmed && (
          <Alert severity="success" sx={{ mt: 1, mb: 2 }}>
            {language === 'hindi' ? 'हस्ताक्षर सफलतापूर्वक लिया गया' : 'Signature captured successfully'}
          </Alert>
        )}

        {/* Checkboxes */}
        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {language === 'hindi' ? (
                  <>
                    मैंने सभी{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setTermsExpanded(true);
                      }}
                      underline="hover"
                    >
                      एरिया मैनेजर नियम व शर्तें
                    </Link>
                    {' '}पढ़ ली हैं और उन्हें स्वीकार करता/करती हूं
                  </>
                ) : (
                  <>
                    I have read and accept the{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setTermsExpanded(true);
                      }}
                      underline="hover"
                    >
                      Area Manager Rules & Conditions
                    </Link>
                    {' '}and agree to be bound by them
                  </>
                )}
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToPrivacy}
                onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {language === 'hindi' ? (
                  <>
                    मैं{' '}
                    <Link href="#" underline="hover">
                      गोपनीयता नीति
                    </Link>
                    {' '}पढ़ चुका/चुकी हूं और अपने व्यक्तिगत डेटा के संग्रह और प्रसंस्करण के लिए सहमति देता/देती हूं
                  </>
                ) : (
                  <>
                    I have read and agree to the{' '}
                    <Link href="#" underline="hover">
                      Privacy Policy
                    </Link>
                    {' '}and consent to the collection and processing of my personal data
                  </>
                )}
              </Typography>
            }
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 3, mb: 2, py: 1.5 }}
          disabled={loading || !signature || !signatureConfirmed || !agreedToTerms || !agreedToPrivacy}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            language === 'hindi' ? 'समझौता सबमिट करें और आगे बढ़ें' : 'Submit Agreement & Continue'
          )}
        </Button>
      </form>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #006994 0%, #00acc1 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Verified sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              {activeStep === 0 ? 'Become a Manager' : activeStep === 1 ? 'Verify OTP' : language === 'hindi' ? 'समझौता' : 'Agreement'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {activeStep === 0
                ? 'Register to start managing partners and services'
                : activeStep === 1
                ? 'Enter the OTP sent to your mobile number'
                : language === 'hindi'
                ? 'समीक्षा करें और समझौता स्वीकार करें'
                : 'Review and accept the agreement'}
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
          {activeStep === 2 && renderStep3()}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {activeStep === 0
                ? 'Already have an account? '
                : 'Back to '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ cursor: 'pointer' }}
              >
                {activeStep === 0 ? 'Sign in here' : 'Sign in'}
              </Link>
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="white">
            © 2026 Friends Services. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;